import { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  HStack,
  Image,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';
import { IFinancialSentEmail, IFinancialSentFile, IFinancialSentRow } from '@partiu/api/dashboard/types.ts';
import { useFinancialSentEmails } from '@partiu/api/dashboard/hooks.ts';
import { categoryBadge } from './badges.ts';
import {
  agreementYears,
  fieldProps,
  FinancialCard,
  FinancialNotice,
  FinancialPagination,
  FinancialShell,
  financialErrorMessage,
  tableSx,
} from './shared.tsx';

const MONTHS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];
const TABLE_TAG = '{{volumetria}}';
const SIGNATURE_TAG = '{{assinatura}}';
const SIGNATURE_SRC = '/financial/assinatura-financeiro.jpg';
const PAGE_SIZES = [25, 50, 100];

interface SentFilters {
  year: number | '';
  month: number | '';
  mode: '' | 'manual' | 'automatic';
  email: string;
  q: string;
}

const EMPTY_FILTERS: SentFilters = { year: '', month: '', mode: '', email: '', q: '' };

function periodLabel(month: number, year: number) {
  return `${String(month).padStart(2, '0')}/${year}`;
}

function shortFileName(filename: string, max = 22) {
  if (filename.length <= max) return filename;
  const dot = filename.lastIndexOf('.');
  const ext = dot > 0 ? filename.slice(dot) : '';
  const base = dot > 0 ? filename.slice(0, dot) : filename;
  const room = max - ext.length - 1;
  if (room < 8) return `${filename.slice(0, max - 1)}…`;
  return `${base.slice(0, room)}…${ext}`;
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function splitBody(body: string, tags: string[]) {
  const unique = [...new Set(tags.filter(Boolean))];
  if (unique.length === 0) return [body];
  return body.split(new RegExp(`(${unique.map(escapeRegExp).join('|')})`, 'g'));
}

function rowTone(row: IFinancialSentRow) {
  if (!row.fill) return undefined;
  return { backgroundColor: `#${row.fill}`, color: `#${row.font ?? '000000'}`, fontWeight: 700 };
}

function SentFileLinks({ files }: { files: IFinancialSentFile[] }) {
  if (files.length === 0) {
    return (
      <Text fontSize="sm" color="gray.400">
        —
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={1.5} maxW="220px">
      {files.map((file) =>
        file.url ? (
          <Box
            key={file.id}
            as="a"
            href={file.url}
            download={file.filename}
            target="_blank"
            rel="noopener noreferrer"
            title={file.filename}
            onClick={(event) => event.stopPropagation()}
            display="inline-flex"
            alignItems="center"
            px={2.5}
            py={1}
            borderRadius="full"
            borderWidth="1px"
            borderColor="gray.200"
            bg="gray.50"
            fontSize="xs"
            fontWeight="600"
            color="gray.700"
            _hover={{ bg: 'white', borderColor: 'gray.300' }}
          >
            {shortFileName(file.filename, 26)}
          </Box>
        ) : (
          <Text key={file.id} fontSize="xs" color="gray.500" title={file.filename}>
            {shortFileName(file.filename, 26)}
          </Text>
        )
      )}
    </VStack>
  );
}

function EmailList({ value }: { value: string }) {
  const items = value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  if (items.length === 0) {
    return (
      <Text fontSize="sm" color="gray.400">
        —
      </Text>
    );
  }
  return (
    <VStack align="start" spacing={0}>
      {items.map((item) => (
        <Text key={item} fontSize="sm" color="gray.700" noOfLines={1}>
          {item}
        </Text>
      ))}
    </VStack>
  );
}

function SentWhen({ value }: { value: string }) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return <Text fontSize="sm">{value}</Text>;
  return (
    <Box whiteSpace="nowrap">
      <Text fontSize="sm" fontWeight="600" color="gray.800">
        {date.toLocaleDateString('pt-BR')}
      </Text>
      <Text fontSize="xs" color="gray.500">
        {date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
      </Text>
    </Box>
  );
}

const EMAIL_LINK = /\{\{painel:([^}]+)\}\}|https?:\/\/[^\s<]+/g;

function EmailBodyText({ text }: { text: string }) {
  const items: Array<{ kind: 'text' | 'link'; value: string }> = [];
  let cursor = 0;
  for (const match of text.matchAll(EMAIL_LINK)) {
    const start = match.index ?? 0;
    if (start > cursor) items.push({ kind: 'text', value: text.slice(cursor, start) });
    items.push({ kind: 'link', value: match[1] || match[0] });
    cursor = start + match[0].length;
  }
  if (cursor < text.length) items.push({ kind: 'text', value: text.slice(cursor) });

  return (
    <Text fontSize="sm" whiteSpace="pre-wrap">
      {items.map((item, index) =>
        item.kind === 'text' ? (
          <span key={`text-${index}`}>{item.value}</span>
        ) : (
          <Button
            key={`link-${index}`}
            as="a"
            href={item.value}
            target="_blank"
            size="sm"
            my={2}
            bg="gray.900"
            color="white"
            _hover={{ opacity: 0.9 }}
          >
            Acessar a volumetria
          </Button>
        )
      )}
    </Text>
  );
}

function SentBodyPreview({ email }: { email: IFinancialSentEmail }) {
  const parts = splitBody(email.body || '', [TABLE_TAG, SIGNATURE_TAG]);
  const tableFile = email.files.find((file) => file.kind === 'image' && file.role === 'table' && file.url);
  const extraImages = email.files.filter((file) => file.kind === 'image' && file.role !== 'table' && file.url);

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4} bg="white" color="gray.800">
      {parts.map((part, index) => {
        if (part === TABLE_TAG) {
          if (!tableFile?.url) return null;
          return (
            <Image key={`table-${index}`} src={tableFile.url} alt={tableFile.filename} maxW="400px" my={3} />
          );
        }
        if (part === SIGNATURE_TAG) {
          return <Image key={`signature-${index}`} src={SIGNATURE_SRC} alt="" maxW="280px" my={3} />;
        }
        if (!part) return null;
        return <EmailBodyText key={`text-${index}`} text={part} />;
      })}
      {extraImages.map((file) => (
        <Image key={file.id} src={file.url as string} alt={file.filename} maxW="400px" my={3} />
      ))}
    </Box>
  );
}

function ModeBadge({ automatic }: { automatic: boolean }) {
  return (
    <Badge
      borderRadius="full"
      px={2.5}
      py={0.5}
      textTransform="none"
      fontWeight="600"
      bg={automatic ? 'blue.50' : 'purple.50'}
      color={automatic ? 'blue.700' : 'purple.700'}
    >
      {automatic ? 'Automático' : 'Manual'}
    </Badge>
  );
}

export function SentEmailsPage() {
  const { colors, extraData } = useTheme();
  const primary = colors.primaryColor || '#1a202c';
  const years = agreementYears(extraData.startDate, extraData.createdAt);
  const [draft, setDraft] = useState<SentFilters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<SentFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [selected, setSelected] = useState<IFinancialSentEmail | null>(null);
  const { data, isLoading, isError, error, isFetching } = useFinancialSentEmails({
    page,
    pageSize,
    year: applied.year === '' ? undefined : applied.year,
    month: applied.year !== '' && applied.month !== '' ? applied.month : undefined,
    email: applied.email.trim() || undefined,
    q: applied.q.trim() || undefined,
    automatic: applied.mode === '' ? undefined : applied.mode === 'automatic',
  });
  const rows = data?.results ?? [];
  const attachmentsOf = (email: IFinancialSentEmail) => email.files.filter((file) => file.kind === 'attachment');

  const applyFilters = () => {
    setApplied({
      ...draft,
      month: draft.year === '' ? '' : draft.month,
      email: draft.email.trim(),
      q: draft.q.trim(),
    });
    setPage(1);
  };

  return (
    <FinancialShell title="E-mails enviados" description="Envios de volumetria deste convênio." maxW="1280px">
        <FinancialCard p={4}>
        <HStack spacing={3} align="flex-end" flexWrap="wrap">
          <Box minW="120px" maxW="140px">
            <Text fontSize="xs" mb={1.5} color="gray.500" fontWeight="600">
              Ano
            </Text>
            <Select
              value={draft.year === '' ? '' : String(draft.year)}
              onChange={(event) => {
                const next = event.target.value;
                setDraft((current) => ({
                  ...current,
                  year: next === '' ? '' : Number(next),
                  month: next === '' ? '' : current.month,
                }));
              }}
              {...fieldProps}
              bg="white"
            >
              <option value="">Todos</option>
              {years.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </Select>
          </Box>
          <Box minW="150px" maxW="180px">
            <Text fontSize="xs" mb={1.5} color="gray.500" fontWeight="600">
              Mês
            </Text>
            <Select
              value={draft.month === '' ? '' : String(draft.month)}
              isDisabled={draft.year === ''}
              onChange={(event) => {
                const next = event.target.value;
                setDraft((current) => ({ ...current, month: next === '' ? '' : Number(next) }));
              }}
              {...fieldProps}
              bg="white"
            >
              <option value="">Todos</option>
              {MONTHS.map((label, index) => (
                <option key={label} value={index + 1}>
                  {label}
                </option>
              ))}
            </Select>
          </Box>
          <Box minW="140px" maxW="160px">
            <Text fontSize="xs" mb={1.5} color="gray.500" fontWeight="600">
              Envio
            </Text>
            <Select
              value={draft.mode}
              onChange={(event) =>
                setDraft((current) => ({ ...current, mode: event.target.value as SentFilters['mode'] }))
              }
              {...fieldProps}
              bg="white"
            >
              <option value="">Todos</option>
              <option value="manual">Manual</option>
              <option value="automatic">Automático</option>
            </Select>
          </Box>
          <Box minW="180px" flex="1">
            <Text fontSize="xs" mb={1.5} color="gray.500" fontWeight="600">
              E-mail
            </Text>
            <Input
              value={draft.email}
              onChange={(event) => setDraft((current) => ({ ...current, email: event.target.value }))}
              placeholder="Destinatário ou cópia"
              {...fieldProps}
              bg="white"
            />
          </Box>
          <Box minW="180px" flex="1">
            <Text fontSize="xs" mb={1.5} color="gray.500" fontWeight="600">
              Texto
            </Text>
            <Input
              value={draft.q}
              onChange={(event) => setDraft((current) => ({ ...current, q: event.target.value }))}
              placeholder="Assunto ou corpo"
              {...fieldProps}
              bg="white"
            />
          </Box>
          <Button bg={primary} color="white" borderRadius="lg" px={6} _hover={{ opacity: 0.9 }} onClick={applyFilters}>
            Filtrar
          </Button>
        </HStack>
        </FinancialCard>

        {isError ? (
          <FinancialNotice tone="error">{financialErrorMessage(error)}</FinancialNotice>
        ) : isLoading ? (
          <FinancialCard p={10}>
            <Spinner display="block" mx="auto" />
          </FinancialCard>
        ) : (
          <FinancialCard>
            {rows.length === 0 ? (
              <Text p={8} textAlign="center" color="gray.500" fontSize="sm">
                Nenhum e-mail enviado com esses filtros.
              </Text>
            ) : (
              <Box overflowX="auto">
                {isFetching && <Spinner size="sm" m={3} />}
                <Table size="sm" sx={tableSx}>
                  <Thead>
                    <Tr>
                      <Th>Enviado em</Th>
                      <Th>Período</Th>
                      <Th>Para</Th>
                      <Th>Cópia</Th>
                      <Th>Assunto</Th>
                      <Th>Anexos</Th>
                      <Th>Envio</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map((row) => (
                      <Tr key={row.id} cursor="pointer" _hover={{ bg: 'gray.50' }} onClick={() => setSelected(row)}>
                        <Td>
                          <SentWhen value={row.sentAt} />
                        </Td>
                        <Td whiteSpace="nowrap">
                          <Badge borderRadius="full" px={2} textTransform="none" bg="gray.100" color="gray.700">
                            {periodLabel(row.month, row.year)}
                          </Badge>
                        </Td>
                        <Td maxW="240px">
                          <EmailList value={row.toEmails} />
                        </Td>
                        <Td maxW="240px">
                          <EmailList value={row.ccEmails} />
                        </Td>
                        <Td maxW="320px">
                          <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={2}>
                            {row.subject}
                          </Text>
                        </Td>
                        <Td>
                          <SentFileLinks files={attachmentsOf(row)} />
                        </Td>
                        <Td>
                          <ModeBadge automatic={row.automatic} />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
            <HStack px={4} py={3} borderTopWidth="1px" borderColor="gray.100" justify="space-between" align="center" flexWrap="wrap" spacing={3}>
              <HStack spacing={2}>
                <Text fontSize="xs" color="gray.500" fontWeight="600">
                  Por página
                </Text>
                <Select
                  value={String(pageSize)}
                  onChange={(event) => {
                    setPageSize(Number(event.target.value));
                    setPage(1);
                  }}
                  {...fieldProps}
                  bg="white"
                  maxW="88px"
                >
                  {PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </Select>
              </HStack>
              <FinancialPagination
                flush
                page={page}
                count={data?.count}
                hasPrevious={!!data?.previous}
                hasNext={!!data?.next}
                isLoading={isFetching}
                onPrevious={() => setPage((current) => Math.max(1, current - 1))}
                onNext={() => setPage((current) => current + 1)}
                itemLabel={['e-mail', 'e-mails']}
              />
            </HStack>
          </FinancialCard>
        )}

        <Modal isOpen={selected !== null} onClose={() => setSelected(null)} size="xl" scrollBehavior="inside">
          <ModalOverlay bg="blackAlpha.400" />
          {selected && (
            <ModalContent borderRadius="2xl">
              <ModalHeader pr={12} fontSize="lg" letterSpacing="-0.02em">
                {selected.subject}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack align="stretch" spacing={4}>
                  <HStack spacing={2} flexWrap="wrap">
                    <Text fontSize="sm" color="gray.500">
                      {new Date(selected.sentAt).toLocaleString('pt-BR')}
                      {' · '}
                      {periodLabel(selected.month, selected.year)}
                    </Text>
                    <ModeBadge automatic={selected.automatic} />
                  </HStack>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Para
                    </Text>
                    <Text fontSize="sm">{selected.toEmails || '—'}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Cópia
                    </Text>
                    <Text fontSize="sm">{selected.ccEmails || '—'}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      Corpo
                    </Text>
                    <Box bg="gray.50" borderRadius="xl" p={1}>
                      <SentBodyPreview email={selected} />
                    </Box>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Anexos
                    </Text>
                    <VStack align="stretch" spacing={1} mt={1}>
                      {attachmentsOf(selected).length === 0 ? (
                        <Text fontSize="sm" color="gray.500">
                          Nenhum anexo.
                        </Text>
                      ) : (
                        attachmentsOf(selected).map((file) =>
                          file.url ? (
                            <Link key={file.id} href={file.url} download={file.filename} isExternal color={primary}>
                              {file.filename}
                            </Link>
                          ) : (
                            <Text key={file.id} fontSize="sm">
                              {file.filename}
                            </Text>
                          )
                        )
                      )}
                    </VStack>
                  </Box>
                  {selected.rows.length > 0 && (
                    <Box maxW="420px" borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th>Plano</Th>
                            <Th isNumeric>Total</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {selected.rows.map((row) => (
                            <Tr key={row.key}>
                              <Td sx={rowTone(row)}>
                                <HStack spacing={2}>
                                  {categoryBadge(row.plan) && (
                                    <Image src={categoryBadge(row.plan) as string} alt="" boxSize="28px" />
                                  )}
                                  <Text fontSize="sm">{row.plan}</Text>
                                </HStack>
                              </Td>
                              <Td isNumeric sx={rowTone(row)}>
                                {row.total}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  )}
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button variant="ghost" onClick={() => setSelected(null)}>
                  Fechar
                </Button>
              </ModalFooter>
            </ModalContent>
          )}
        </Modal>
    </FinancialShell>
  );
}
